import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Docsector } from 'src/app/models/obs/docsector';
import { Sector } from 'src/app/models/obs/sector';
import { DocsectorService } from 'src/app/services/obs/docsector.service';
import { SectorService } from 'src/app/services/obs/sector.service';
import { UploadfileService } from 'src/app/services/util/uploadfile.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-adm-form-doc-sector',
  templateUrl: './adm-form-doc-sector.component.html',
  styleUrls: ['./adm-form-doc-sector.component.scss']
})
export class AdmFormDocSectorComponent implements OnInit {
  idTier1: number;
  form: FormGroup;
  docsector: Docsector = {};
  sectores: Sector[] = [];
  reports: Sector[] = [];
  edit: boolean = false;

  showPdfControls: boolean = false;
  filenamePDF: string = '';
  uploatedFile: File;
  isLoading: boolean = false;
  uploadType: 'file' | 'url' = 'file';
  originalUploadType: 'file' | 'url' = 'file';

  constructor(
    private docsectorS: DocsectorService,
    private sectorS: SectorService,
    private uploadS: UploadfileService,
    private router: Router,
    private fb: FormBuilder,
    private activedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.idTier1 = this.activedRoute.snapshot.params['id'];
    this.createForm();
    this.loadData();
  }

  createForm() {
    this.form = this.fb.group({
      id: [null],
      url: [null, Validators.pattern(/^https?:\/\/.+\..+/)],
      doc_name: [''],
      idsector: [null, Validators.required],
      idtier1: [this.idTier1],
      status: [true],
      uploadType: ['file']
    });
  }

  loadData() {
    forkJoin([
      this.getAllDocSector(),
      this.getSectores()
    ]).subscribe(([docsectorData, sectoresData]) => {
      this.reports = docsectorData['data'];
      this.sectores = sectoresData['data'];

      const sectorMapping = {};
      this.sectores.forEach(sector => {
        sectorMapping[sector.id] = sector.longname;
      });
      this.reports.forEach(report => {
        report.longname = sectorMapping[report['idsector']];
      });
    });
  }

  sectorSeleccionado(selectedSectorId: number): boolean {
    return this.reports.some(report => report['idsector'] === selectedSectorId);
  }

  onPdfChange(event: any) {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      this.uploatedFile = uploadedFile;
      this.filenamePDF = uploadedFile.name;
      this.form.get('doc_name').setValue(uploadedFile.name);
      this.showPdfControls = true;
    }
  }

  removePdf() {
    const currentUrl = this.form.get('url').value;
    const isPdf = currentUrl && currentUrl.includes('/uploads/reporte/sectorial/');

    if (isPdf) {
      const message = '¿Estás seguro de eliminar permanentemente el documento "' +
                     this.filenamePDF + '"?\nEsta acción no se puede deshacer.';

      if (confirm(message)) {
        this.deletePreviousFile().then(() => {
          this.form.get('url').setValue('');
          this.form.get('doc_name').setValue('');
          this.filenamePDF = '';
          this.uploatedFile = null;
          this.showPdfControls = false;

          if (this.edit && this.originalUploadType === 'file') {
            this.uploadType = 'url';
            this.form.get('url').enable();
          }
        });
      }
    } else {
      this.form.get('url').setValue('');
      this.form.get('doc_name').setValue('');
      this.showPdfControls = false;
    }
  }

  getSectores() {
    return this.sectorS.getAll();
  }

  getAllDocSector() {
    return this.docsectorS.getAllByIdt1(this.idTier1);
  }

  save() {
    if (this.form.invalid) {
      return Object.values(this.form.controls).forEach(control => {
        control.markAsTouched();
      });
    }

    // Validación especial para cambio de PDF a URL
    if (this.edit && this.originalUploadType === 'file' && this.uploadType === 'url') {
      const urlValue = this.form.get('url').value;
      if (!urlValue || urlValue.trim() === '') {
        alert('Por favor ingresa una URL válida');
        return;
      }
    }

    if (this.uploadType === 'file' && this.uploatedFile) {
      this.onUpload();
    } else {
      const urlValue = this.form.get('url').value;
      if (this.uploadType === 'url' && (!urlValue || urlValue.trim() === '')) {
        alert('Por favor ingresa una URL válida');
        return;
      }

      if (urlValue && !urlValue.startsWith('http')) {
        this.form.get('url').setValue(`https://${urlValue}`);
      }
      this.saveOrUpdate();
    }
  }

  private deletePreviousFile(): Promise<void> {
    return new Promise((resolve, reject) => {
      const currentUrl = this.form.get('url').value;
      if (currentUrl && currentUrl.includes('/uploads/reporte/sectorial/')) {
        const filename = currentUrl.split('/').pop();
        this.uploadS.deletedFileRepoSec(filename).subscribe({
          next: () => resolve(),
          error: (err) => {
            console.error('Error eliminando PDF:', err);
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  }

  private onUpload() {
    this.isLoading = true;
    const deletePromise = this.edit && this.originalUploadType === 'file'
      ? this.deletePreviousFile()
      : Promise.resolve();

    deletePromise.then(() => {
      const formDataPDF = new FormData();
      const uploadedFile = this.uploatedFile;
      formDataPDF.append("uploads", uploadedFile, uploadedFile.name);

      this.uploadS.uploadFileRepoSec(formDataPDF).subscribe({
        next: (response: any) => {
          if (response) {
            const filenamePDF = response.filename || '';
            this.form.get('url').setValue(`/uploads/reporte/sectorial/${filenamePDF}`);
            this.form.get('doc_name').setValue(uploadedFile.name);
            this.showPdfControls = true;
            this.saveOrUpdate();
          }
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        }
      });
    });
  }

  private saveOrUpdate() {
    this.edit ? this.update(this.form.value.id, this.form.value) : this.create(this.form.value);
  }

  private create(docsector: Docsector) {
    delete docsector.id;
    this.docsectorS.create(docsector).subscribe({
      next: () => {
        this.clearForm();
        this.loadData();
        this.isLoading = false;
      },
      error: (err) => console.error(err)
    });
  }

  private update(id: string, docsector: Docsector) {
    delete this.docsector.id;
    this.docsectorS.update(id, docsector).subscribe({
      next: () => {
        this.clearForm();
        this.loadData();
        this.isLoading = false;
      },
      error: (err) => console.error(err)
    });
  }

  editReport(report: Docsector) {
    this.form.patchValue(report);
    this.docsector = report;
    this.edit = true;

    if (report.url?.startsWith('http')) {
      this.uploadType = 'url';
      this.originalUploadType = 'url';
      this.form.get('url').enable();
    } else {
      this.uploadType = 'file';
      this.originalUploadType = 'file';
      this.filenamePDF = report.doc_name;
      this.showPdfControls = !!report.url;
      if (report.url) {
        this.form.get('url').disable();
      }
    }
  }

  clearForm() {
    this.form.reset();
    this.form.get('idtier1').setValue(this.idTier1);
    this.form.get('status').setValue(true);
    this.showPdfControls = false;
    this.edit = false;
    this.filenamePDF = '';
  }

  campoNoValido(campo: string) {
    return this.form.get(campo).invalid && this.form.get(campo).touched;
  }

  cancel() {
    const currentPath = window.location.pathname;
    const regex = /\/adm\/\w+\//;
    const match = currentPath.match(regex);
    const dynamicUrl = match ? match[0] : '';
    this.router.navigate([dynamicUrl + 'list/']);
  }
}
